import { useState, useEffect } from 'react';

interface Tag {
  id: number;
  name: string;
}

interface TagSelectorProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  className?: string;
}

export default function TagSelector({
  selectedTags,
  onTagsChange,
  className = '',
}: TagSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Tag[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const query = searchQuery.trim();
    const controller = new AbortController();

    if (!query) {
      setSearchResults([]);
      setIsSearching(false);
      return () => controller.abort();
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(
          `/admin/tags/search?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        if (!controller.signal.aborted) {
          setSearchResults(
            data.tags.filter(
              (tag: Tag) =>
                !selectedTags.some(selected => selected.id === tag.id)
            )
          );
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error searching tags:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchQuery, selectedTags]);

  const handleAddTag = (tag: Tag) => {
    if (!selectedTags.some(selected => selected.id === tag.id)) {
      onTagsChange([...selectedTags, tag]);
    }
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleRemoveTag = (tagId: number) => {
    onTagsChange(selectedTags.filter(tag => tag.id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!searchQuery.trim()) return;

    try {
      const response = await fetch('/admin/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') || '',
        },
        body: JSON.stringify({ tag: { name: searchQuery.trim() } }),
      });

      if (response.ok) {
        const newTag = await response.json();
        handleAddTag(newTag);
      } else {
        const error = await response.json();
        console.error('Error creating tag:', error);
      }
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-base-content mb-2">
        Tags
      </label>

      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedTags.map(tag => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-white text-sm rounded-full font-medium"
            >
              {tag.name}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag.id)}
                className="ml-1 hover:bg-blue-700 rounded-full p-0.5"
                aria-label={`Remove ${tag.name} tag`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search or create tags..."
          className="w-full px-3 py-2 border border-base-300 rounded-lg bg-base-100 text-base-content focus:ring-2 focus:ring-primary focus:border-primary"
        />

        {/* Dropdown */}
        {showDropdown && (searchQuery || searchResults.length > 0) && (
          <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {isSearching ? (
              <div className="p-3 text-center text-base-content/60">
                Searching...
              </div>
            ) : searchResults.length > 0 ? (
              <div>
                {searchResults.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleAddTag(tag)}
                    className="w-full text-left px-3 py-2 hover:bg-base-200 text-base-content"
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            ) : searchQuery.trim() ? (
              <div className="p-3">
                <button
                  type="button"
                  onClick={handleCreateTag}
                  className="w-full text-left px-3 py-2 hover:bg-base-200 text-base-content"
                >
                  Create "{searchQuery.trim()}"
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
}
